/**
 * Hook for rule overrides (PM bypass + audit)
 * Phase D: Rule Engine
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

interface OverrideInput {
  projectId: string;
  ruleCode: string;
  reason: string;
  context: Record<string, unknown>;
  userId: string;
  module: string;
  entityLabel?: string;
}

export const useRuleOverride = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: OverrideInput) => {
      // 1. Insert override record
      const { error: overrideError } = await supabase
        .from('rule_overrides' as any)
        .insert({
          project_id: input.projectId,
          rule_code: input.ruleCode,
          reason: input.reason,
          context: input.context,
          user_id: input.userId,
        } as any);

      if (overrideError) throw overrideError;

      // 2. Insert activity log entry with danger highlight
      const { error: activityError } = await supabase
        .from('project_activity_log' as any)
        .insert({
          project_id: input.projectId,
          user_id: input.userId,
          action_type: 'override',
          module: input.module,
          entity_type: 'rule',
          entity_id: null,
          entity_label: input.entityLabel || `Rule ${input.ruleCode}`,
          old_value: null,
          new_value: { rule_code: input.ruleCode, reason: input.reason },
          metadata: input.context,
          highlight_type: 'danger',
        } as any);

      if (activityError) {
        logger.warn('[useRuleOverride] Activity log insert failed:', activityError);
        // Non-critical — don't throw
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rule-override-count', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-activity-log', variables.projectId] });
      toast.success('Override đã được ghi nhận');
    },
    onError: (error) => {
      logger.error('[useRuleOverride] Error:', error);
      toast.error('Không thể ghi nhận override');
    },
  });
};

export const useOverrideCount = (projectId: string | undefined) => {
  return useQuery({
    queryKey: ['rule-override-count', projectId],
    queryFn: async (): Promise<number> => {
      if (!projectId) return 0;

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count, error } = await supabase
        .from('rule_overrides' as any)
        .select('id', { count: 'exact', head: true })
        .eq('project_id', projectId)
        .gte('created_at', startOfMonth.toISOString());

      if (error) {
        logger.error('[useOverrideCount] Error:', error);
        return 0;
      }

      return count || 0;
    },
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  });
};
